/**
 * O AVISO PERSISTENTE da busca do plano do fabricante — um por trabalho,
 * visível em QUALQUER conversa e QUALQUER rota.
 *
 * Por que ele existe: até aqui a espera da busca vivia dentro do turno
 * (`SearchWaitPanel`), então ela só existia enquanto o usuário estava olhando
 * aquela conversa. Trocou de conversa e o sinal sumia, embora o trabalho
 * continuasse rodando no servidor. Este aviso é o único elemento que sobrevive
 * à troca de conversa e de rota.
 *
 * REUSO, NÃO REINVENÇÃO: o sistema de aviso deste repositório é o
 * `react-hot-toast` montado por `AppToaster` — hoje o único. Ele já tem
 * permanência (`duration: Infinity`) e aviso clicável. O par morto que este
 * cabeçalho mandava evitar (`shared/components/Toast.tsx` +
 * `shared/stores/ui.ts`) foi removido no B2; a trava que impede a volta dele
 * está em `components/AppToaster.aviso-unico.test.ts`.
 *
 * O CLIQUE VIVE NO PRÓPRIO CONTEÚDO (um `<button>` de verdade, não um `div`
 * com `onClick`), de propósito: assim o aviso navega mesmo quando a página que
 * o renderiza não é o `AppToaster`, e o leitor de tela anuncia um botão.
 *
 * NÃO É TELA. É elemento da tela do chat, no molde dos outros avisos.
 */
import toast from 'react-hot-toast'
import { Search, CircleCheck, TriangleAlert } from 'lucide-react'
import i18n from './i18n/config'
import { useChatStore } from '../stores/chatStore'
import type { ResearchJobSnapshot } from '../stores/researchJobsStore'

/** O aviso do estado final some sozinho este tanto depois de chegar. */
export const NOTICE_DISMISS_AFTER_MS = 10_000

/** Prefixo do id do aviso — um id estável por trabalho evita aviso duplicado. */
export function researchNoticeId(jobId: string): string {
  return `research-${jobId}`
}

/** 21_000 → "00:21" · 125_000 → "02:05". */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const min = Math.floor(total / 60)
  const sec = total % 60
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

/** 300_000 → "5min", pela via do i18n (o sufixo muda de idioma). */
export function formatCeiling(ms: number): string {
  return i18n.t('research.notice.ceilingMinutes', { count: Math.max(1, Math.round(ms / 60_000)) })
}

function noticeText(job: ResearchJobSnapshot, now: number): string {
  if (job.status === 'ready') return i18n.t('research.notice.ready', { label: job.label })
  if (job.status === 'failed') return i18n.t('research.notice.failed', { label: job.label })
  return i18n.t('research.notice.running', {
    label: job.label,
    elapsed: formatElapsed(now - job.startedAt),
    ceiling: formatCeiling(job.ceilingMs),
  })
}

function NoticeBody({
  job,
  now,
  onOpen,
}: {
  job: ResearchJobSnapshot
  now: number
  onOpen: () => void
}) {
  const Icon = job.status === 'ready' ? CircleCheck : job.status === 'failed' ? TriangleAlert : Search
  return (
    <button
      type="button"
      data-testid={`research-notice-${job.jobId}`}
      onClick={onOpen}
      aria-label={i18n.t('research.notice.openAria')}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        background: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        color: 'inherit',
        font: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <Icon size={16} aria-hidden="true" />
      <span>{noticeText(job, now)}</span>
    </button>
  )
}

/** O que já foi desenhado para cada trabalho — evita reemitir o aviso final. */
const rendered = new Map<string, ResearchJobStatusKey>()
type ResearchJobStatusKey = ResearchJobSnapshot['status']

/**
 * Decide se o aviso flutuante de UM trabalho deve aparecer agora. A busca em
 * si nunca para por causa desta função — ela só manda no AVISO.
 *
 * Mesma guarda de `shouldSkipToast` (`lib/toast.ts`), extraída em forma pura
 * (parâmetros explícitos em vez de ler `document`/store direto) para caber na
 * tabela do contrato e no teste: aba escondida nunca cala, conversa sem dono
 * nunca cala, só cala quando a aba está visível E é a mesma conversa do
 * trabalho — porque aí quem avisa é o painel da própria tela
 * (`shouldShowSearchWaitPanel`), não o aviso flutuante.
 *
 * O estado `ready` é exceção e avisa em qualquer combinação: o plano do
 * fabricante CHEGOU é um evento novo que o usuário precisa ver, esteja onde
 * estiver. O `failed` NÃO é mais exceção (Defeito 3 da STO-1890): o motor já
 * grava a frase de falha NA PRÓPRIA conversa (`avisarPlanoNaConversa`), então
 * avisar de novo pelo toast quando o usuário está olhando a mesma conversa é o
 * aviso duplicado. Fora da conversa, o toast continua avisando.
 */
export function deveMostrarAvisoDaBusca(
  job: ResearchJobSnapshot,
  activeConversationId: string | null,
  visibilityState: DocumentVisibilityState,
): boolean {
  if (job.status === 'ready') return true
  if (visibilityState === 'hidden') return true
  if (activeConversationId === null) return true
  return activeConversationId !== job.conversationId
}

/**
 * Põe a tela em dia com o estado dos trabalhos. Chamada a cada segundo pelo
 * `ResearchJobsNotifier` — idempotente por `jobId`.
 *
 * NÃO FILTRA `jobs` POR CONVERSA ATIVA, e isso é a alma do desenho: filtrar a
 * LISTA aqui (ou montar isto dentro da página do chat) faria o aviso sumir de
 * vez quando o usuário trocasse de conversa, que é exatamente o defeito que a
 * sprint anterior consertou. O que muda de conversa em conversa é só se o
 * aviso de CADA trabalho aparece agora (`deveMostrarAvisoDaBusca`) — a busca
 * continua sendo rastreada para todo `jobId`, sempre.
 */
export function syncResearchNotices(
  jobs: Record<string, ResearchJobSnapshot>,
  now: number,
  onOpen: (conversationId: string) => void,
  onExpire: (jobId: string) => void,
): void {
  // Trabalho que saiu do estado leva o aviso junto.
  for (const jobId of [...rendered.keys()]) {
    if (!(jobId in jobs)) {
      // `remove` e não `dismiss`: dispensar deixa o nó na tela com opacidade 0
      // para a animação de saída. Aqui o trabalho já saiu do estado — o aviso
      // tem de sair do DOM, senão ele fica ocupando a fila de avisos para sempre.
      toast.remove(researchNoticeId(jobId))
      rendered.delete(jobId)
    }
  }

  for (const job of Object.values(jobs)) {
    const id = researchNoticeId(job.jobId)
    const terminal = job.status === 'ready' || job.status === 'failed'

    if (terminal && job.settledAt != null && now - job.settledAt >= NOTICE_DISMISS_AFTER_MS) {
      // Dispensa (com animação de saída) e tira o trabalho do estado. A varredura
      // do começo desta função, na passada seguinte, arranca o nó do DOM — por
      // isso `rendered` NÃO se limpa aqui.
      toast.dismiss(id)
      onExpire(job.jobId)
      continue
    }

    // O aviso final é emitido UMA vez: reemitir a cada segundo reiniciaria o
    // relógio de dispensa do react-hot-toast e ele nunca sumiria sozinho.
    if (terminal && rendered.get(job.jobId) === job.status) continue

    const ativa = useChatStore.getState().activeConversationId
    if (!deveMostrarAvisoDaBusca(job, ativa, document.visibilityState)) {
      // O usuário está na tela, olhando este trabalho — quem avisa é o
      // painel da própria conversa, não o aviso flutuante. `remove` (não
      // `dismiss`) tira o nó do DOM na hora, sem deixar rastro de fila; e
      // limpa `rendered` para que o aviso volte a nascer se o usuário sair
      // desta conversa antes do trabalho terminar.
      toast.remove(id)
      rendered.delete(job.jobId)
      continue
    }

    toast(<NoticeBody job={job} now={now} onOpen={() => onOpen(job.conversationId)} />, {
      id,
      duration: Infinity,
      ariaProps: { role: 'status', 'aria-live': 'polite' },
    })
    rendered.set(job.jobId, job.status)
  }
}
